import {
  ChangeEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  ButtonBase,
  Chip,
  IconButton,
  Slider,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useSnackbar } from 'notistack';

import ButtonDownloadMidia from 'components/ButtonDownloadMidia';
import MessageBase, { MessageBaseProps } from 'components/MessageBase';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch } from 'store';
import {
  setTranscricaoSolicitada,
  setTranscricaoSolicitadaGrupo,
  transcreverAudio,
} from 'store/reducers/mensagens';
import { calculateMediaTime } from 'utils/calculateMediaTime';

import ExpandableTranscript from './ExpandableTranscript';
import MessageErrorFallback from './MessageErrorFallback';

const TRANSCRIPTION_TIMEOUT_MS = 120_000;

type AudioMessageProps = MessageBaseProps;

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    minWidth: spacing(40),
  },
  progressBar: {
    width: '100%',
    color: 'inherit',
    margin: spacing(0, 2),
  },
  playbackRate: {
    border: 0,
    minWidth: spacing(6),
    backgroundColor: `${palette.action.selected} !important`,
    color: 'inherit',
    '& > *': {
      fontWeight: '600',
    },
  },
  time: {
    fontSize: typography.pxToRem(10),
    position: 'absolute',
    right: spacing(8),
    top: spacing(2.5),
  },
  transcribeButton: {
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  tooltip: {
    fontSize: '11px',
  },
}));

export default function AudioMessage(props: AudioMessageProps) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const audioRef = useRef<HTMLAudioElement>(null);
  const dispatch = useAppDispatch();
  const currentUser = useCurrentUser();
  const [duration, setDuration] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const mensagemId = props.mensagem?.id ?? '';
  const atendimentoId = props.mensagem?.atendimentoId ?? '';
  const grupoChatId = props.mensagem?.grupoChatId ?? '';
  const isAtendimento = !!atendimentoId;
  const audioTranscription =
    props.mensagem?.messageMetadata?.audioTranscription ?? null;
  const transcriptionStatus = audioTranscription?.status ?? null;
  const transcriptionStatusAt = audioTranscription?.statusAt ?? null;
  const isRequested = transcriptionStatus === 'requested';
  const isFailed = transcriptionStatus === 'failed';

  useEffect(() => {
    if (percentage >= 100 && isPlaying) {
      setIsPlaying(false);
      setPercentage(0);
    }
  }, [isPlaying, percentage]);

  useEffect(() => {
    if (!isRequested || !transcriptionStatusAt) return undefined;

    const elapsed = Date.now() - new Date(transcriptionStatusAt).getTime();
    const remaining = TRANSCRIPTION_TIMEOUT_MS - elapsed;

    if (remaining <= 0) {
      dispatch(
        isAtendimento
          ? setTranscricaoSolicitada({
              mensagemId,
              atendimentoId,
              status: 'failed',
            })
          : setTranscricaoSolicitadaGrupo({
              mensagemId,
              grupoChatId,
              status: 'failed',
            }),
      );
      return undefined;
    }

    const timer = setTimeout(() => {
      dispatch(
        isAtendimento
          ? setTranscricaoSolicitada({
              mensagemId,
              atendimentoId,
              status: 'failed',
            })
          : setTranscricaoSolicitadaGrupo({
              mensagemId,
              grupoChatId,
              status: 'failed',
            }),
      );
    }, remaining);

    return () => clearTimeout(timer);
  }, [
    isRequested,
    transcriptionStatusAt,
    mensagemId,
    atendimentoId,
    grupoChatId,
    isAtendimento,
    dispatch,
  ]);

  try {
    const {
      mensagem: {
        midiaUrl,
        midiaDuracao,
        midiaDownloadErro,
        baixando,
        grupoChatId,
        mensagem,
      },
    } = props;

    const togglePlayPause = () => {
      const audio = audioRef.current;
      const prevValue = isPlaying;

      setIsPlaying(!prevValue);

      if (audio) {
        if (!prevValue) audio.play();
        else audio.pause();
      }
    };

    const togglePlaybackRate = () => {
      const audio = audioRef.current;
      const newPlaybackRate = playbackRate === 2 ? 1 : playbackRate + 0.5;

      if (audio) {
        audio.playbackRate = newPlaybackRate;
        setPlaybackRate(newPlaybackRate);
      }
    };

    const handleChangeProgress = (
      _event: ChangeEvent<Record<string, unknown>>,
      newValue: number | number[],
    ) => {
      const audio = audioRef.current;
      const value = newValue as number;
      if (audio) {
        audio.currentTime = (audio.duration / 100) * value;
        setPercentage(value);
      }
    };

    const getCurrDuration = (event: SyntheticEvent<HTMLAudioElement>) => {
      const { currentTarget } = event;
      const time = currentTarget.currentTime;
      const percent = (
        (currentTarget.currentTime / currentTarget.duration) *
        100
      ).toFixed(2);

      setPercentage(+percent);
      setCurrentTime(Number(time.toFixed(2)));
    };

    const handleTranscribe = () => {
      dispatch(
        isAtendimento
          ? setTranscricaoSolicitada({ mensagemId, atendimentoId })
          : setTranscricaoSolicitadaGrupo({ mensagemId, grupoChatId }),
      );

      dispatch(transcreverAudio(mensagemId))
        .unwrap()
        .catch(error => {
          enqueueSnackbar(
            error.message ??
              'Falha ao solicitar transcrição. Por favor, tente novamente.',
            {
              variant: 'error',
            },
          );

          dispatch(
            isAtendimento
              ? setTranscricaoSolicitada({
                  mensagemId,
                  atendimentoId,
                  status: 'failed',
                })
              : setTranscricaoSolicitadaGrupo({
                  mensagemId,
                  grupoChatId,
                  status: 'failed',
                }),
          );
        });
    };

    const hasPermission = currentUser?.modulos?.can_transcribe_audio ?? false;

    return (
      <MessageBase {...props}>
        <Box>
          <Box className={classes.root}>
            <audio
              ref={audioRef}
              src={midiaUrl}
              onTimeUpdate={getCurrDuration}
              onLoadedData={() => {
                setDuration(midiaDuracao ?? 0);
              }}
            >
              <track kind="captions" />
            </audio>

            {midiaDownloadErro ? (
              <ButtonDownloadMidia
                mensagemId={mensagemId}
                atendimentoId={atendimentoId}
                grupoChatId={grupoChatId}
                baixando={baixando}
              />
            ) : (
              <ButtonBase onClick={togglePlayPause} disabled={!midiaUrl}>
                <Box clone fontSize="2rem">
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </Box>
              </ButtonBase>
            )}

            <Slider
              className={classes.progressBar}
              value={percentage}
              onChange={handleChangeProgress}
              disabled={midiaDownloadErro || !midiaUrl}
            />

            <Chip
              size="small"
              className={classes.playbackRate}
              label={`${playbackRate}x`}
              onClick={togglePlaybackRate}
              disabled={midiaDownloadErro || !midiaUrl}
            />

            <Typography className={classes.time}>
              {isPlaying
                ? calculateMediaTime(currentTime)
                : calculateMediaTime(duration)}
            </Typography>
          </Box>

          {mensagem ? (
            <Box style={{ paddingTop: 8, paddingLeft: 8 }}>
              <ExpandableTranscript text={mensagem} fromMe={props.fromMe} />
            </Box>
          ) : null}

          {!mensagem && !props.fromMe && hasPermission && (
            <Box style={{ paddingTop: 8, paddingLeft: 8 }}>
              {isFailed && (
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip
                    title="A transcrição pode estar indisponível porque nenhuma palavra foi detectada na mensagem de voz, o áudio está corrompido ou há problemas técnicos no serviço."
                    arrow
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <InfoOutlinedIcon
                      style={{
                        fontSize: 16,
                        color: 'inherit',
                        opacity: 0.6,
                        marginRight: 4,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                  <Typography
                    variant="body2"
                    style={{
                      color: 'inherit',
                      opacity: 0.6,
                      fontStyle: 'italic',
                      fontSize: '12px',
                      marginRight: 4,
                    }}
                  >
                    Transcrição indisponível
                  </Typography>
                  <Tooltip
                    title="Tentar novamente"
                    arrow
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <IconButton
                      size="small"
                      onClick={handleTranscribe}
                      style={{ padding: 2, color: 'inherit', opacity: 0.6 }}
                    >
                      <RefreshIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {!isFailed && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleTranscribe}
                  disabled={isRequested}
                  disableElevation
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  className={classes.transcribeButton}
                  startIcon={
                    isRequested ? <CircularProgress size={16} /> : undefined
                  }
                  style={{ fontSize: '12px' }}
                >
                  {isRequested ? 'Transcrevendo...' : 'Transcrever'}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </MessageBase>
    );
  } catch (error) {
    return <MessageErrorFallback {...props} />;
  }
}
